/**
 * YouTubeLightbox Component
 * 
 * Modal overlay for playing YouTube videos.
 * Used in the Dashboard Tutorials tab.
 */

import React, { useEffect, useCallback } from 'react';
import './YouTubeLightbox.css';

interface YouTubeLightboxProps {
  videoId: string;
  title?: string;
  onClose: () => void;
}

export const YouTubeLightbox: React.FC<YouTubeLightboxProps> = ({ videoId, title, onClose }) => {
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const embedSrc = `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0&modestbranding=1&playsinline=1&enablejsapi=1&origin=${encodeURIComponent(origin)}`;

  // Handle escape key to close
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    }
  }, [onClose]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    // Prevent body scroll when lightbox is open
    document.body.style.overflow = 'hidden';
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [handleKeyDown]);

  // Handle backdrop click
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div className="youtube-lightbox-overlay" onClick={handleBackdropClick}>
      <div className="youtube-lightbox-container">
        <button className="youtube-lightbox-close" onClick={onClose} aria-label="Close">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path
              d="M18 6L6 18M6 6L18 18"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
        
        {title && <h3 className="youtube-lightbox-title">{title}</h3>}
        
        <div className="youtube-lightbox-video">
          <iframe
            src={embedSrc}
            title={title || 'YouTube video'}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            referrerPolicy="strict-origin-when-cross-origin"
            allowFullScreen
          />
        </div>
      </div>
    </div>
  );
};

export default YouTubeLightbox;
